import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { integrationCompaniesApi, STORAGE_BASE_URL } from '../../services/adminApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
const SortableItem = ({ company, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: company.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const tableCellStyle = {
    padding: '1rem 1.5rem',
    fontSize: '0.875rem',
    color: '#111827',
    borderBottom: '1px solid #e5e7eb'
  };

  const statusButtonStyle = (isActive) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.625rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
    backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
    color: isActive ? '#166534' : '#dc2626',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s, color 0.2s'
  });

  const actionLinkStyle = {
    color: '#2563eb',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginRight: '0.75rem',
    transition: 'color 0.2s'
  };

  const actionButtonStyle = {
    color: '#dc2626',
    textDecoration: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'color 0.2s'
  };

  const dragHandleStyle = {
    cursor: 'grab',
    padding: '0.5rem',
    color: '#6b7280',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2rem',
    height: '2rem',
    borderRadius: '0.25rem',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    transition: 'all 0.2s'
  };

  const logoStyle = {
    width: '4rem',
    height: '4rem',
    borderRadius: '0.5rem',
    objectFit: 'cover',
    border: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
    color: '#6b7280'
  };

  const categoryBadgeStyle = {
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.375rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    marginBottom: '0.25rem',
    display: 'inline-block'
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      <td style={tableCellStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div
            {...listeners}
            style={dragHandleStyle} 
            title="Sürükleyerek sıralayın"
          >
            ⋮⋮
          </div>
          <div style={logoStyle}>
            {company.logo ? (
              <img 
                src={company.logo.startsWith('http') ? company.logo : `${STORAGE_BASE_URL}${company.logo}`} 
                alt={company.name}
                style={{ width: '100%', height: '100%', borderRadius: '0.5rem', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div style={{ 
              display: company.logo ? 'none' : 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              fontSize: '1rem',
              color: '#6b7280'
            }}>
              🏢
            </div>
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#111827' }}>
              {company.name}
            </div>
            <div style={categoryBadgeStyle}>
              {company.category_title}
            </div>
            {company.description && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280', maxWidth: '300px' }}>
                {company.description}
              </div>
            )}
          </div>
        </div>
      </td>
      <td style={tableCellStyle}>
        <span style={statusButtonStyle(company.is_active)}>
          {company.is_active ? 'Aktif' : 'Pasif'}
        </span>
      </td>
      <td style={tableCellStyle}>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {company.sort_order || 0}
        </div>
      </td>
      <td style={tableCellStyle}>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {new Date(company.created_at).toLocaleDateString('tr-TR')}
        </div>
      </td>
      <td style={tableCellStyle}>
        <Link
          to={`/admin/integration-companies/${company.id}/edit`}
          style={actionLinkStyle}
          onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
          onMouseOut={(e) => e.target.style.color = '#2563eb'}
        >
          Düzenle
        </Link>
        <button
          onClick={() => onDelete(company.id)}
          style={actionButtonStyle}
          onMouseOver={(e) => e.target.style.color = '#b91c1c'}
          onMouseOut={(e) => e.target.style.color = '#dc2626'}
        >
          Sil
        </button>
      </td>
    </tr>
  );
};

const IntegrationCompaniesList = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await integrationCompaniesApi.getAll();
      
      if (response.data.success) {
        const companiesData = response.data.data?.data || response.data.data || [];
        setCompanies(companiesData);
        setError(null);
      } else {
        setError('Entegrasyon firmaları yüklenemedi');
      }
    } catch (err) {
      console.error('Companies fetch error:', err);
      setError('Entegrasyon firmaları yüklenirken hata oluştu: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu entegrasyon firmasını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await integrationCompaniesApi.delete(id);
      if (response.data.success) {
        await fetchCompanies();
        alert('Entegrasyon firması başarıyla silindi');
      } else {
        alert('Entegrasyon firması silinemedi');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Entegrasyon firması silinirken hata oluştu');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = companies.findIndex(item => item.id === active.id);
      const newIndex = companies.findIndex(item => item.id === over.id);
      
      const newCompanies = arrayMove(companies, oldIndex, newIndex);
      setCompanies(newCompanies);

      try {
        // Group companies by category_id and update sort_order for each category
        const categoryGroups = {};
        newCompanies.forEach((company, index) => {
          if (!categoryGroups[company.category_id]) {
            categoryGroups[company.category_id] = [];
          }
          categoryGroups[company.category_id].push({ ...company, newIndex: index });
        });

        // Update sort_order for each company
        const updatePromises = Object.keys(categoryGroups).map(categoryId => {
          const categoryCompanies = categoryGroups[categoryId];
          return Promise.all(categoryCompanies.map((company, categoryIndex) => {
            return integrationCompaniesApi.update(company.id, { sort_order: categoryIndex + 1 });
          }));
        });

        await Promise.all(updatePromises.flat());
        
        // Update local state with new sort_order values
        setCompanies(prevCompanies => 
          prevCompanies.map((company, index) => ({
            ...company,
            sort_order: index + 1
          }))
        );
      } catch (error) {
        console.error('Error updating sort order:', error);
        // Revert on error
        await fetchCompanies();
      }
    }
  };

  const containerStyle = {
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  };

  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#111827',
    margin: 0
  };

  const buttonStyle = {
    backgroundColor: '#3b82f6',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '0.375rem',
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'background-color 0.2s'
  };

  const tableStyle = {
    backgroundColor: 'white',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    width: '100%',
    borderCollapse: 'collapse'
  };

  const tableHeaderStyle = {
    backgroundColor: '#f9fafb',
    padding: '0.75rem 1.5rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb'
  };

  const loadingStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '16rem',
    padding: '2rem'
  };

  const spinnerStyle = {
    width: '2rem',
    height: '2rem',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  const errorStyle = {
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  };

  const errorTextStyle = {
    color: '#dc2626',
    marginBottom: '1rem'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>Entegrasyon firmaları yükleniyor...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={errorStyle}>
          <p style={errorTextStyle}>{error}</p>
          <button
            onClick={fetchCompanies}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Entegrasyon Firmaları - Admin Panel</title>
      </Helmet>
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Entegrasyon Firmaları</h1>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Link to="/admin/integration-categories" style={buttonStyle}>
              Kategoriler
            </Link>
            <Link to="/admin/integration-companies/create" style={buttonStyle}>
              Yeni Firma Ekle
            </Link>
          </div>
        </div>

        {companies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            Henüz entegrasyon firması bulunmamaktadır.
          </div>
        ) : (
          <div style={tableStyle}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Firma Bilgileri</th>
                    <th style={tableHeaderStyle}>Durum</th>
                    <th style={tableHeaderStyle}>Sıralama</th>
                    <th style={tableHeaderStyle}>Oluşturulma Tarihi</th>
                    <th style={tableHeaderStyle}>İşlemler</th>
                  </tr>
                </thead>
                <SortableContext
                  items={companies.map(company => company.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {companies.map((company) => (
                      <SortableItem
                        key={company.id}
                        company={company}
                        onEdit={() => {}}
                        onDelete={handleDelete}
                      />
                    ))}
                  </tbody>
                </SortableContext>
              </table>
            </DndContext>
          </div>
        )}
      </div>
    </>
  );
};

export default IntegrationCompaniesList;
